import { Component, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Chip, CircularProgress, Divider, Grid,
    LinearProgress, MenuItem, Select, Skeleton, TextField, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import PublicIcon from "@mui/icons-material/Public";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ReplayIcon from "@mui/icons-material/Replay";
import { useSnackbar } from "notistack";
import PageTitle from "../PageTitle";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { apiConstants, getGeneric, patchGeneric, postGeneric } from "../../common/constants";
import { formatDate, parseDateAndTime } from "../../helpers/UtilityHelper";
import OrderView from "../EscalationManagement/OrderView";
import SectionCard, { MUTED, NAVY, cardHeadSx, cardIcSx, cardSx, gradientBtnSx } from "./SectionCard";
import { ASSIGNEE_TTL_MS, cachedGet, invalidateCached } from "./escalationApiCache";
import { SEVERITY_META, SEVERITY_OPTIONS } from "./severity";
import { ACCENTS, COLORS, HERO_BG, rise } from "./emsTheme";
import { SeverityPill, StatusPill, SubStatusPill, TatBadge, statusMeta } from "./TicketsUi";
import { prettyStatus } from "./analyticsLabels";
import { PLATFORM_META, platformOf } from "./platform";

/** A ticket's or remark's files share one 20 MB limit per upload (EscalationDocService). */
const MAX_REMARK_FILES_BYTES = 20 * 1024 * 1024;
/** The file's own name from a signed S3 link (keys are "escalation/uploads/<uuid>-<name>"). */
const fileNameFromUrl = (url, i) => {
    try {
        const last = decodeURIComponent(new URL(url).pathname.split("/").pop() || "");
        return last.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, "") || `Attachment ${i + 1}`;
    } catch (e) {
        return `Attachment ${i + 1}`;
    }
};

const LABEL_SX = { fontSize: 12, color: MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" };

const Field = ({ label, value, color }) => (
    <Box>
        <Typography sx={LABEL_SX}>{label}</Typography>
        <Typography sx={{ fontSize: 14, color: color || NAVY, fontWeight: 600, mt: 0.25, overflowWrap: "anywhere" }}>{value || "—"}</Typography>
    </Box>
);

const UNASSIGNED = { id: "", name: "Unassigned", email: "" };

/** Severity, editable in place — saving leaves an internal remark ("Severity changed from P2 to P0"). */
const SeverityField = ({ ticketId, escalation, onChanged }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [saving, setSaving] = useState(false);
    const value = escalation.severity || "";

    const change = async (severity) => {
        if (!severity || severity === value) return;
        setSaving(true);
        try {
            await patchGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}/severity`, { severity });
            enqueueSnackbar(`Severity set to ${severity}`, { variant: "success" });
            onChanged();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Could not change severity", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Typography sx={LABEL_SX}>Severity</Typography>
            <Select size="small" fullWidth value={value} disabled={saving} displayEmpty
                onChange={(e) => change(e.target.value)}
                sx={{ mt: 0.5, fontSize: 14, fontWeight: 600, color: SEVERITY_META[value]?.color || NAVY, borderRadius: "10px" }}>
                {!value && <MenuItem value="" disabled>Not set</MenuItem>}
                {SEVERITY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
        </Box>
    );
};

/** "Assigned To" as a searchable dropdown — Prozo staff whose merchant access covers this
 *  ticket's merchant (GET /assignable-users); picking one saves right away (PATCH /assignee),
 *  and the backend leaves an internal remark recording the change. If the list can't be loaded
 *  (e.g. a merchant-scoped login, which can't assign), it stays a read-only value. */
const AssigneeField = ({ ticketId, escalation, onAssigned }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [users, setUsers] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        cachedGet(`${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}/assignable-users`, ASSIGNEE_TTL_MS)
            .then((response) => { if (!cancelled) setUsers(response?.data || []); })
            .catch(() => { if (!cancelled) setUsers(null); });
        return () => { cancelled = true; };
    }, [ticketId]);

    const options = useMemo(() => {
        // Alphabetical by the name shown — the backend sorts on fullName, which puts users who
        // have none (shown by email) first.
        const sorted = [...(users || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
        const list = [UNASSIGNED, ...sorted];
        // Someone assigned earlier may since have lost access to this merchant — still show them
        // as the current value rather than a blank box.
        if (escalation.assignedToUserId && !list.some((u) => u.id === escalation.assignedToUserId)) {
            list.push({ id: escalation.assignedToUserId, name: escalation.assignedToUserName || "Unknown user", email: "" });
        }
        return list;
    }, [users, escalation.assignedToUserId, escalation.assignedToUserName]);

    if (users === null) return <Field label="Assigned To" value={escalation.assignedToUserName} />;

    const current = options.find((u) => u.id === (escalation.assignedToUserId || "")) || UNASSIGNED;

    const assign = async (user) => {
        const userId = user?.id || "";
        if (userId === (escalation.assignedToUserId || "")) return;
        setSaving(true);
        try {
            await patchGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}/assignee`, { userId });
            enqueueSnackbar(userId ? `Assigned to ${user.name}` : "Ticket unassigned", { variant: "success" });
            invalidateCached("/assignees"); // the Assignee filter may now have a new name
            onAssigned();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Could not assign this ticket", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Typography sx={LABEL_SX}>Assigned To</Typography>
            <Autocomplete
                size="small"
                disableClearable
                disabled={saving}
                options={options}
                value={current}
                onChange={(e, user) => assign(user)}
                getOptionLabel={(u) => u.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderOption={(props, u) => (
                    <li {...props} key={u.id || "unassigned"}>
                        <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{u.name}</Typography>
                            {u.email && u.email !== u.name && <Typography sx={{ fontSize: 12, color: MUTED }}>{u.email}</Typography>}
                        </Box>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Search by name or email"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (<>{saving && <CircularProgress size={14} />}{params.InputProps.endAdornment}</>),
                        }}
                    />
                )}
                filterOptions={(opts, { inputValue }) => {
                    const q = inputValue.trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter((u) => u.name.toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
                }}
                sx={{ mt: 0.5, "& .MuiInputBase-root": { fontSize: 14, fontWeight: 600, borderRadius: "10px" } }}
            />
        </Box>
    );
};

// customFieldValues keys are camelCase storage keys (e.g. "invoiceCycle"), not display labels —
// CSS text-transform:uppercase alone would mash them into "INVOICECYCLE" with no spaces.
const humanizeKey = (key) => key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());

// Same excludes list ViewEms.js (V1) sends to order/search — kept identical so both pages get
// the same shaped response from the same generic order-search endpoint.
const ORDER_SEARCH_EXCLUDES = "returnInfo,itemList.hsn,deliveryTime,itemList.itemUrl,itemList.itemDescription,actualDeliveryTat,proshipDeliveryTat,courierDeliveryTat,actualPickUpTime,lastStatusUpdateTime,edd,epd,lifeCycleComplete,labelUrl,courier,merchantDetail,courierId,version,order_history,orderManifest,invoiceUrl,courierPricingRTO,courierPricing,overriddenCourierAccountCode,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,createdByUser,skipRule,modifiedByUser,rvp_reason,channel_name,customer_detail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchant,merchantPricing.merchantRateCard,merchantPricing.pricingObj,courierPricingRTO,modifiedByUser,lastModifiedDate,terminalStatus,merchantPricingRTO.merchantRateCard,merchantPricingRTO.pricingObj";

/** Shipment details come from the old EMS's OrderView and the order's own data; if either
 *  trips over an odd order, show a note instead of blanking the whole ticket page. */
class ShipmentErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error) {
        console.error("Shipment details failed to render", error);
    }

    render() {
        if (this.state.failed) {
            return <Typography sx={{ textAlign: "center", fontSize: 13, color: MUTED, py: 2 }}>Shipment details couldn't be shown for this AWB.</Typography>;
        }
        return this.props.children;
    }
}

const relatedTicketColumns = (navigate) => [
    {
        field: 'escalationNumber', headerAlign: "left", headerName: 'Ticket #', flex: 0.4,
        align: 'left', renderCell: ({ row }) => row?.escalationNumber,
    },
    {
        field: 'awbNumber', headerAlign: "left", headerName: 'AWB', flex: 0.5,
        align: 'left', renderCell: ({ row }) => row?.awbNumber || "-",
    },
    { field: 'merchantName', headerAlign: "left", headerName: 'Merchant', flex: 0.5, align: 'left' },
    { field: 'category', headerAlign: "left", headerName: 'Category', flex: 0.5, align: 'left' },
    { field: 'subCategory', headerAlign: "left", headerName: 'SubCategory', flex: 0.5, align: 'left' },
    {
        field: 'status', headerAlign: "left", headerName: 'Ticket Status', flex: 0.35,
        align: 'left', renderCell: ({ row }) => row?.status,
    },
    {
        field: 'createdAt', headerAlign: "left", headerName: 'Created Date', flex: 0.35,
        align: 'left', renderCell: ({ row }) => row?.createdAt ? formatDate(row.createdAt) : '-',
    },
    {
        field: 'updatedAt', headerAlign: "left", headerName: 'Last Updated', flex: 0.35,
        align: 'left', renderCell: ({ row }) => row?.updatedAt ? formatDate(row.updatedAt) : '-',
    },
    {
        // A fixed width: at flex 0.25 the header was cut to "Actio".
        field: 'action', headerAlign: "left", headerName: 'Action', width: 90, minWidth: 90, align: 'left', sortable: false,
        renderCell: ({ row }) => (
            <VisibilityIcon
                fontSize="small"
                sx={{ color: COLORS.purple, cursor: 'pointer' }}
                onClick={() => navigate(`/tools/escalation-v2/view?ticketId=${row.id}`)}
            />
        ),
    },
];

const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";

const CountBadge = ({ n }) => (
    <Box component="span" sx={{ minWidth: 24, textAlign: "center", px: 0.875, borderRadius: "999px", fontSize: 12, fontWeight: 800, lineHeight: "22px",
        bgcolor: COLORS.purpleSoft, color: COLORS.purple, flexShrink: 0 }}>{n}</Box>
);

// The two collapsible sections, styled as cards with the same header as SectionCard.
const ACCORDION_SX = { ...cardSx, "&::before": { display: "none" } };
const ACCORDION_SUMMARY_SX = { ...cardHeadSx, borderBottom: "none", minHeight: 0, "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.25, my: 0, minWidth: 0 },
    "&.Mui-expanded": { minHeight: 0 } };

/** Escalation Desk V2 — ticket detail view. Reached by clicking a ticket # in
 *  EscalationManagementV2/index.js (route: /tools/escalation-v2/view?ticketId=...), same
 *  separate-route pattern V1's ViewEms.js uses rather than a modal. Uses the
 *  GET/PATCH/POST /api/escalation/{id}* endpoints that already existed from Phase 0, plus
 *  the same generic order/search endpoint and OrderView.js component V1's page uses for the
 *  Shipment (AWB) Details section — that component isn't Freshdesk-specific, just order data,
 *  so it's reused as-is rather than rebuilt. */
const ViewTicket = () => {
    const [searchParams] = useSearchParams();
    const ticketId = searchParams.get("ticketId");
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [isLoading, setIsLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [statusList, setStatusList] = useState([]);
    const [statusDraft, setStatusDraft] = useState("");
    const [subStatusDraft, setSubStatusDraft] = useState("");
    const [statusReason, setStatusReason] = useState("");
    const [statusSubmitting, setStatusSubmitting] = useState(false);
    const [remarkBody, setRemarkBody] = useState("");
    const [remarkVisibility, setRemarkVisibility] = useState("INTERNAL");
    const [remarkSubmitting, setRemarkSubmitting] = useState(false);
    const [remarkFiles, setRemarkFiles] = useState([]);
    const remarkFileInput = useRef(null);
    const remarkFilesBytes = remarkFiles.reduce((sum, f) => sum + f.size, 0);
    const [orderSearch, setOrderSearch] = useState(null);
    const [orderLoading, setOrderLoading] = useState(false);
    // The order lookup waits until Shipment (AWB) Details is first opened; it's collapsed by default.
    const [shipmentRequested, setShipmentRequested] = useState(false);
    const [relatedTickets, setRelatedTickets] = useState([]);
    const [relatedPage, setRelatedPage] = useState(0);
    const [relatedSizePerPage, setRelatedSizePerPage] = useState(10);

    // Only the latest load paints: opening a related ticket while the previous one is still
    // loading must not leave the previous ticket on screen when its slower response lands.
    const detailRequestRef = useRef(0);
    const loadDetail = useCallback(async () => {
        if (!ticketId) return;
        const request = ++detailRequestRef.current;
        setIsLoading(true);
        try {
            const response = await getGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}`);
            if (request !== detailRequestRef.current) return;
            setDetail(response?.data || null);
            setStatusDraft(response?.data?.escalation?.status || "");
            setSubStatusDraft(response?.data?.escalation?.subStatus || "");
        } catch (error) {
            if (request === detailRequestRef.current) enqueueSnackbar("Could not load this ticket", { variant: "error" });
        } finally {
            if (request === detailRequestRef.current) setIsLoading(false);
        }
    }, [ticketId, enqueueSnackbar]);

    useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    // Opening another ticket from "Other Related Tickets" keeps this page mounted, so without
    // this the new ticket loads while the view stays scrolled down at the bottom.
    const topRef = useRef(null);
    useEffect(() => {
        topRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
        window.scrollTo({ top: 0, behavior: "smooth" });
        setRelatedPage(0);
    }, [ticketId]);

    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const response = await cachedGet(apiConstants.ESCALATION_STATUS_CONFIG_V2);
                setStatusList(response?.data || []);
            } catch (error) {
                enqueueSnackbar("Could not load the status list", { variant: "error" });
            }
        };
        fetchStatuses();
    }, [enqueueSnackbar]);

    const escalation = detail?.escalation;
    const primaryAwb = escalation?.awbNumber || null;

    // Shipment (AWB) Details — same order/search call + OrderView.js render as V1's ViewEms.js.
    useEffect(() => {
        if (!primaryAwb) { setOrderSearch(null); return undefined; }
        if (!shipmentRequested) return undefined;
        let cancelled = false;
        const fetchOrder = async () => {
            setOrderLoading(true);
            try {
                const response = await postGeneric(apiConstants.ORDER_LISTS, {
                    excludes: ORDER_SEARCH_EXCLUDES,
                    andfilter: [{ field: 'awbNumber', operator: 'eq', value: primaryAwb }],
                });
                if (!cancelled) setOrderSearch(response);
            } catch (error) {
                if (!cancelled) setOrderSearch(null);
            } finally {
                if (!cancelled) setOrderLoading(false);
            }
        };
        fetchOrder();
        return () => { cancelled = true; };
    }, [primaryAwb, shipmentRequested]);

    // Other Related Tickets — other escalations sharing the same primary AWB.
    useEffect(() => {
        if (!primaryAwb) { setRelatedTickets([]); return undefined; }
        let cancelled = false;
        const fetchRelated = async () => {
            try {
                const response = await postGeneric(apiConstants.ESCALATION_SEARCH_V2, { awbNumber: primaryAwb, size: 50 });
                const others = (response?.data?.results || []).filter((t) => t.id !== ticketId);
                if (!cancelled) setRelatedTickets(others);
            } catch (error) {
                if (!cancelled) setRelatedTickets([]);
            }
        };
        fetchRelated();
        return () => { cancelled = true; };
    }, [primaryAwb, ticketId]);

    const customFieldEntries = useMemo(
        () => Object.entries(escalation?.customFieldValues || {}),
        [escalation]
    );

    const statusLabelToCategory = useMemo(() => {
        const m = {};
        statusList.forEach((s) => { m[s.label] = s.category; });
        return m;
    }, [statusList]);

    // Closed By / Re-Opened By — V1 reads these off dedicated Freshdesk fields; V2 has no such
    // fields, so they're derived from the status-history trail instead (same information, same
    // "who and when" the CTO discussion's status/sub-status taxonomy will eventually formalize).
    const closedByEntry = useMemo(() => {
        const history = detail?.statusHistory || [];
        for (let i = history.length - 1; i >= 0; i--) {
            if (statusLabelToCategory[history[i].toStatus] === "CLOSED") return history[i];
        }
        return null;
    }, [detail, statusLabelToCategory]);

    const reopenedByEntry = useMemo(() => {
        if (!escalation?.reopenCount) return null;
        const history = detail?.statusHistory || [];
        for (let i = history.length - 1; i >= 0; i--) {
            const fromCat = statusLabelToCategory[history[i].fromStatus];
            const toCat = statusLabelToCategory[history[i].toStatus];
            if ((fromCat === "CLOSED" || fromCat === "RESOLVED") && toCat === "OPEN") return history[i];
        }
        return null;
    }, [detail, escalation, statusLabelToCategory]);

    const statusUnchanged = statusDraft === escalation?.status;
    const subStatusUnchanged = subStatusDraft === (escalation?.subStatus || "");

    const submitStatusChange = async () => {
        if (!statusDraft || (statusUnchanged && subStatusUnchanged)) return;
        setStatusSubmitting(true);
        try {
            await patchGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}/status`, {
                status: statusDraft,
                reason: statusReason || undefined,
                subStatus: subStatusDraft,
            });
            enqueueSnackbar("Status updated", { variant: "success" });
            setStatusReason("");
            loadDetail();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Could not update status", { variant: "error" });
        } finally {
            setStatusSubmitting(false);
        }
    };

    const addRemarkFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = "";
        setRemarkFiles((prev) => [...prev, ...picked.filter((f) => !prev.some((p) => p.name === f.name && p.size === f.size))]);
    };

    const submitRemark = async () => {
        if (!remarkBody.trim() && remarkFiles.length === 0) return;
        if (remarkFilesBytes > MAX_REMARK_FILES_BYTES) {
            enqueueSnackbar("Files can be 20 MB in total. Remove some and try again.", { variant: "error" });
            return;
        }
        setRemarkSubmitting(true);
        try {
            const data = { body: remarkBody, visibility: remarkVisibility };
            if (remarkFiles.length > 0) {
                const formData = new FormData();
                formData.append("data", JSON.stringify(data));
                remarkFiles.forEach((file) => formData.append("attachments", file));
                await postGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}/remark`, formData);
            } else {
                await postGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/${ticketId}/remark`, data);
            }
            enqueueSnackbar("Remark added", { variant: "success" });
            setRemarkBody("");
            setRemarkFiles([]);
            loadDetail();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Could not add remark", { variant: "error" });
        } finally {
            setRemarkSubmitting(false);
        }
    };

    if (!ticketId) {
        return <Typography sx={{ p: 3 }}>No ticket selected.</Typography>;
    }

    const orderMatch = orderSearch?.data?.count === 1 && orderSearch?.data?.data?.[0]?.awb_number === primaryAwb;
    const remarks = detail?.remarks || [];
    const history = (detail?.statusHistory || []).slice().reverse();
    // Resolved/closed tickets: whether they were resolved by their resolution due time.
    const resolvedOnTime = escalation?.resolvedAt && escalation?.resolutionDueAt
        ? new Date(escalation.resolvedAt).getTime() <= new Date(escalation.resolutionDueAt).getTime() : null;

    return (
        <>
            <div ref={topRef} />
            <PageTitle>Escalation {escalation?.escalationNumber || ""}</PageTitle>
            {/* A reload after a change keeps the page on screen; the first load shows placeholders. */}
            {isLoading && detail && (
                <LinearProgress aria-label="Refreshing ticket" sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1400, height: 3, bgcolor: COLORS.purpleSoft,
                    "& .MuiLinearProgress-bar": { background: ACCENTS.purple.grad } }} />
            )}

            <Box sx={{ mb: 1.5 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/tools/escalation-v2")}
                    sx={{ textTransform: "none", fontWeight: 700, color: COLORS.ink2, borderRadius: "999px", px: 1.5,
                        "&:hover": { bgcolor: COLORS.purpleSoft, color: COLORS.purple } }}
                >
                    Back to tickets
                </Button>
            </Box>

            {!escalation && isLoading && (
                <Box sx={{ display: "grid", gap: 2 }} aria-busy="true" aria-label="Loading ticket">
                    <Skeleton variant="rounded" height={190} sx={{ borderRadius: "16px" }} />
                    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.6fr) minmax(0, 1fr)" } }}>
                        <Skeleton variant="rounded" height={360} sx={{ borderRadius: "12px" }} />
                        <Skeleton variant="rounded" height={360} sx={{ borderRadius: "12px" }} />
                    </Box>
                </Box>
            )}

            {escalation && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>

                    {/* ── Hero: who/what/when at a glance ── */}
                    <Box sx={{ position: "relative", overflow: "hidden", borderRadius: "16px", background: HERO_BG, color: "#fff",
                        px: { xs: 2, md: 3.5 }, py: { xs: 2.25, md: 3 }, ...rise(0) }}>
                        <Box aria-hidden sx={{ position: "absolute", right: -70, top: -90, width: 260, height: 260, borderRadius: "50%", border: "1px solid rgba(255,255,255,.14)", pointerEvents: "none" }} />
                        <Box aria-hidden sx={{ position: "absolute", right: 50, top: 30, width: 110, height: 110, borderRadius: "50%", bgcolor: "rgba(255,255,255,.06)", pointerEvents: "none" }} />
                        <Box sx={{ position: "relative", display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
                            <Box aria-hidden sx={{ width: 48, height: 48, borderRadius: "12px", flexShrink: 0, display: "grid", placeItems: "center",
                                bgcolor: "rgba(255,255,255,.16)", border: "1px solid rgba(255,255,255,.24)" }}>
                                <ConfirmationNumberOutlinedIcon sx={{ color: "#fff", fontSize: 26 }} />
                            </Box>
                            <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", opacity: 0.8 }}>Escalation ticket</Typography>
                                <Typography component="h1" sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, lineHeight: 1.25, letterSpacing: "-.01em", color: "#fff", m: 0 }}>
                                    Ticket {escalation.escalationNumber}
                                </Typography>
                                {escalation.awbNumber && (
                                    <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.25 }}>AWB {escalation.awbNumber}</Typography>
                                )}
                                {escalation.subject && (
                                    <Typography sx={{ fontSize: 15, fontWeight: 600, mt: 0.75, opacity: 0.95, overflowWrap: "anywhere" }}>{escalation.subject}</Typography>
                                )}
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                <StatusPill status={escalation.status} category={escalation.statusCategory} height={28} />
                                <SubStatusPill subStatus={escalation.subStatus} height={28} />
                                <SeverityPill severity={escalation.severity} long height={28} />
                            </Box>
                        </Box>

                        <Box sx={{ position: "relative", mt: 2.25, pt: 2, borderTop: "1px solid rgba(255,255,255,.18)", display: "grid", gap: 2,
                            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" } }}>
                            {[
                                { label: "Merchant", value: escalation.merchantName },
                                { label: "Category", value: [escalation.category, escalation.subCategory].filter(Boolean).join(" › ") },
                                { label: "Assigned To", value: escalation.assignedToUserName || "Unassigned" },
                                { label: "Created", value: escalation.createdAt ? formatDate(escalation.createdAt) : null },
                            ].map((m) => (
                                <Box key={m.label} sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", opacity: 0.72 }}>{m.label}</Typography>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25, overflowWrap: "anywhere" }}>{m.value || "—"}</Typography>
                                </Box>
                            ))}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", opacity: 0.72 }}>Resolution TAT</Typography>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, mt: 0.25 }}>
                                    {/* Pending at Client pauses the SLA: the due date moves on when the ticket leaves it. */}
                                    {escalation.slaPausedAt ? "Paused — awaiting brand" : escalation.resolutionDueAt ? parseDateAndTime(escalation.resolutionDueAt) : "—"}
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <TatBadge dueAt={escalation.resolutionDueAt} statusCategory={escalation.statusCategory} showLater />
                                    {resolvedOnTime != null && !["OPEN", "PENDING"].includes(escalation.statusCategory) && (
                                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, height: 22, px: 0.875, borderRadius: "999px", fontSize: 12, fontWeight: 700,
                                            bgcolor: resolvedOnTime ? ACCENTS.green.soft : ACCENTS.red.soft, color: resolvedOnTime ? ACCENTS.green.solid : ACCENTS.red.solid }}>
                                            <TaskAltIcon sx={{ fontSize: 14 }} aria-hidden />{resolvedOnTime ? "Resolved within TAT" : "Resolved after TAT"}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        {escalation.reopenCount > 0 && (
                            <Box sx={{ position: "relative", mt: 1.5, display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.25, py: 0.4, borderRadius: "999px",
                                bgcolor: "rgba(255,255,255,.14)", border: "1px solid rgba(255,255,255,.22)", fontSize: 12, fontWeight: 700 }}>
                                <ReplayIcon sx={{ fontSize: 15 }} aria-hidden />Reopened {escalation.reopenCount} {escalation.reopenCount === 1 ? "time" : "times"}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: "grid", gap: 2, alignItems: "start", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.6fr) minmax(0, 1fr)" } }}>
                        <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
                            <Box sx={rise(1)}>
                                <SectionCard icon={<AssignmentOutlinedIcon />} title="Ticket Details" sub="Overall summary of the ticket">
                                    <Grid container spacing={2}>
                                        {/* Classification — what this ticket is about */}
                                        <Grid item xs={12} sm={6} md={4}><Field label="Category" value={escalation.category} /></Grid>
                                        <Grid item xs={12} sm={6} md={4}><Field label="Sub Category" value={escalation.subCategory} /></Grid>
                                        <Grid item xs={12} sm={6} md={4}><Field label="Merchant" value={escalation.merchantName} /></Grid>
                                        <Grid item xs={12} sm={6} md={4}><Field label="Courier" value={escalation.courierName} /></Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field label="AWB" value={escalation.awbNumber} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field label="Shipment Status" value={escalation.orderStatus ? prettyStatus(escalation.orderStatus) : null} />
                                        </Grid>

                                        <Grid item xs={12}><Divider sx={{ borderColor: COLORS.grid }} /></Grid>

                                        {/* People — who's involved */}
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field label="Platform" value={PLATFORM_META[platformOf(escalation)].longLabel.replace(" Tickets", "")} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field label="Created By" value={escalation.createdByName ? `${escalation.createdByName}${escalation.createdByEmail ? " | " + escalation.createdByEmail : ""}` : null} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field label="Merchant Panel" value={escalation.visibleToMerchant === false
                                                ? "Hidden — internal only, no emails"
                                                : `Visible${escalation.requesterEmail ? " — updates to " + escalation.requesterEmail : ""}`} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}><SeverityField ticketId={ticketId} escalation={escalation} onChanged={loadDetail} /></Grid>
                                        <Grid item xs={12} sm={6} md={4}><AssigneeField ticketId={ticketId} escalation={escalation} onAssigned={loadDetail} /></Grid>

                                        <Grid item xs={12}><Divider sx={{ borderColor: COLORS.grid }} /></Grid>

                                        {/* Timing — when it happened / is due */}
                                        <Grid item xs={12} sm={6} md={4}><Field label="Created" value={escalation.createdAt ? formatDate(escalation.createdAt) : null} /></Grid>
                                        <Grid item xs={12} sm={6} md={4}><Field label="Updated" value={escalation.updatedAt ? formatDate(escalation.updatedAt) : null} /></Grid>
                                        <Grid item xs={12} md={4} sx={{ display: { xs: "none", md: "block" } }} />
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field label="First Response Due" color={ACCENTS.red.solid} value={escalation.firstResponseDueAt ? parseDateAndTime(escalation.firstResponseDueAt) : null} />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Field label="Resolution Due By" color={ACCENTS.red.solid} value={escalation.resolutionDueAt ? parseDateAndTime(escalation.resolutionDueAt) : null} />
                                        </Grid>
                                        <Grid item xs={12} md={4} sx={{ display: { xs: "none", md: "block" } }} />

                                        {escalation.subject && (
                                            <Grid item xs={12}><Field label="Subject" value={escalation.subject} /></Grid>
                                        )}
                                        {escalation.description && (
                                            <Grid item xs={12}>
                                                <Typography sx={LABEL_SX}>Description</Typography>
                                                <Box sx={{ mt: 0.5, p: 1.5, borderRadius: "10px", bgcolor: COLORS.ground, border: `1px solid ${COLORS.grid}` }}>
                                                    <Typography sx={{ fontSize: 14, color: NAVY, fontWeight: 500, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{escalation.description}</Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                        {customFieldEntries.map(([key, value]) => (
                                            <Grid item xs={12} sm={6} md={4} key={key}><Field label={humanizeKey(key)} value={value} /></Grid>
                                        ))}
                                        {escalation.additionalEmails?.length > 0 && (
                                            <Grid item xs={12}><Field label="Additional Emails" value={escalation.additionalEmails.join(", ")} /></Grid>
                                        )}
                                        {closedByEntry && (
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Field label="Closed By" value={`${closedByEntry.changedByName || "—"} · ${formatDate(closedByEntry.changedAt)}`} />
                                            </Grid>
                                        )}
                                        {reopenedByEntry && (
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Field label="Re-Opened By" value={`${reopenedByEntry.changedByName || "—"} · ${formatDate(reopenedByEntry.changedAt)}`} />
                                            </Grid>
                                        )}
                                        {detail?.attachmentUrls?.length > 0 && (
                                            <Grid item xs={12}>
                                                <Typography sx={{ ...LABEL_SX, mb: 0.75 }}>Attachments</Typography>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                    {detail.attachmentUrls.map((url, i) => (
                                                        <Button
                                                            key={i}
                                                            href={url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<AttachFileOutlinedIcon fontSize="small" />}
                                                            sx={{ textTransform: "none", fontSize: 13, fontWeight: 700, borderRadius: "999px", color: COLORS.purple, borderColor: "#D9CCEA",
                                                                "&:hover": { borderColor: COLORS.purple, bgcolor: COLORS.purpleSoft } }}
                                                        >
                                                            Attachment {i + 1}
                                                        </Button>
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </SectionCard>
                            </Box>

                            <Box sx={rise(2)}>
                                <SectionCard icon={<ForumOutlinedIcon />} title="Remarks" sub="Internal notes, or a record of external contact"
                                    headerExtra={remarks.length > 0 ? <CountBadge n={remarks.length} /> : null}>
                                    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, mb: 2 }}>
                                        {remarks.length === 0 && (
                                            <Box component="li" sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5, px: 1.5, borderRadius: "10px", bgcolor: COLORS.ground }}>
                                                <ForumOutlinedIcon sx={{ fontSize: 18, color: COLORS.ink3 }} aria-hidden />
                                                <Typography sx={{ fontSize: 13, color: MUTED }}>No remarks yet.</Typography>
                                            </Box>
                                        )}
                                        {remarks.map((r, i) => {
                                            const external = r.visibility === "EXTERNAL";
                                            const tone = external ? ACCENTS.blue : { solid: COLORS.ink2, soft: "#F3F4F6" };
                                            return (
                                                <Box component="li" key={r.id} sx={{ display: "grid", gridTemplateColumns: "32px minmax(0, 1fr)", gap: 1.25, position: "relative", pb: i === remarks.length - 1 ? 0 : 1.75 }}>
                                                    {i < remarks.length - 1 && (
                                                        <Box aria-hidden sx={{ position: "absolute", left: 15, top: 34, bottom: 0, width: 2, bgcolor: COLORS.grid, borderRadius: 1 }} />
                                                    )}
                                                    <Box aria-hidden sx={{ width: 32, height: 32, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 800,
                                                        color: "#fff", background: external ? ACCENTS.blue.grad : ACCENTS.purple.grad }}>
                                                        {initials(r.authorName)}
                                                    </Box>
                                                    <Box sx={{ border: `1px solid ${COLORS.grid}`, borderRadius: "10px", p: 1.5, bgcolor: external ? "#FAFCFF" : "#fff", minWidth: 0 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{r.authorName}</Typography>
                                                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, height: 22, px: 0.875, borderRadius: "999px",
                                                                fontSize: 12, fontWeight: 700, bgcolor: tone.soft, color: tone.solid }}>
                                                                {external ? <PublicIcon sx={{ fontSize: 14 }} /> : <LockOutlinedIcon sx={{ fontSize: 14 }} />}
                                                                {r.visibility}
                                                            </Box>
                                                            <Typography sx={{ fontSize: 12, color: MUTED, ml: "auto" }}>
                                                                {r.createdAt ? formatDate(r.createdAt) : ""}
                                                            </Typography>
                                                        </Box>
                                                        {r.body && <Typography sx={{ fontSize: 14, color: NAVY, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{r.body}</Typography>}
                                                        {r.attachmentUrls?.length > 0 && (
                                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: r.body ? 1 : 0 }}>
                                                                {r.attachmentUrls.map((url, j) => (
                                                                    <Button key={j} href={url} target="_blank" rel="noopener noreferrer" size="small" variant="outlined"
                                                                        startIcon={<AttachFileOutlinedIcon sx={{ fontSize: 16 }} />}
                                                                        sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 700, borderRadius: "999px", color: COLORS.purple, borderColor: "#D9CCEA",
                                                                            maxWidth: 260, "& .MuiButton-startIcon": { flexShrink: 0 },
                                                                            "&:hover": { borderColor: COLORS.purple, bgcolor: COLORS.purpleSoft } }}>
                                                                        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fileNameFromUrl(url, j)}</Box>
                                                                    </Button>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>

                                    <Box sx={{ p: 1.5, borderRadius: "12px", bgcolor: COLORS.ground, border: `1px solid ${COLORS.grid}` }}>
                                        <TextField
                                            multiline
                                            minRows={3}
                                            fullWidth
                                            placeholder="Add a remark…"
                                            value={remarkBody}
                                            onChange={(e) => setRemarkBody(e.target.value)}
                                            sx={{ mb: 1.5, "& .MuiInputBase-root": { bgcolor: "#fff", borderRadius: "10px", fontSize: 14 } }}
                                        />
                                        {remarkFiles.length > 0 && (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                                                {remarkFiles.map((f, i) => (
                                                    <Chip key={`${f.name}-${i}`} size="small" icon={<AttachFileOutlinedIcon sx={{ fontSize: 15 }} />} label={f.name}
                                                        onDelete={() => setRemarkFiles((prev) => prev.filter((_, j) => j !== i))}
                                                        sx={{ maxWidth: 260, bgcolor: "#fff", border: `1px solid ${COLORS.grid}`, fontWeight: 600, "& .MuiChip-label": { textTransform: "none" } }} />
                                                ))}
                                                <Typography sx={{ fontSize: 12, color: remarkFilesBytes > MAX_REMARK_FILES_BYTES ? ACCENTS.red.solid : MUTED }}>
                                                    {(remarkFilesBytes / (1024 * 1024)).toFixed(1)} of 20 MB
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                                            <input ref={remarkFileInput} type="file" multiple hidden onChange={addRemarkFiles} />
                                            <Button size="small" variant="outlined" startIcon={<AttachFileOutlinedIcon />} onClick={() => remarkFileInput.current?.click()}
                                                sx={{ textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: "10px", bgcolor: "#fff", color: COLORS.ink2, borderColor: COLORS.grid,
                                                    "&:hover": { borderColor: COLORS.purple, color: COLORS.purple } }}>
                                                Attach files
                                            </Button>
                                            <Select
                                                size="small"
                                                value={remarkVisibility}
                                                onChange={(e) => setRemarkVisibility(e.target.value)}
                                                sx={{ minWidth: 160, bgcolor: "#fff", borderRadius: "10px", fontSize: 14 }}
                                            >
                                                <MenuItem value="INTERNAL">Internal</MenuItem>
                                                <MenuItem value="EXTERNAL">External</MenuItem>
                                            </Select>
                                            {remarkVisibility === "EXTERNAL" && (
                                                <Typography sx={{ fontSize: 12, color: escalation.visibleToMerchant === false ? ACCENTS.orange.solid : MUTED, flex: "1 1 200px" }}>
                                                    {escalation.visibleToMerchant === false
                                                        ? "This ticket is hidden from the merchant — they won't see this remark."
                                                        : "The merchant sees this remark. No email is sent for it; it shows in the next status-update email."}
                                                </Typography>
                                            )}
                                            <LoadingButton
                                                loading={remarkSubmitting}
                                                variant="contained"
                                                disabled={!remarkBody.trim() && remarkFiles.length === 0}
                                                onClick={submitRemark}
                                                sx={{ ...gradientBtnSx, ml: "auto" }}
                                            >
                                                Add Remark
                                            </LoadingButton>
                                        </Box>
                                    </Box>
                                </SectionCard>
                            </Box>
                        </Box>

                        <Box sx={{ minWidth: 0, ...rise(2) }}>
                            <SectionCard icon={<HistoryOutlinedIcon />} title="Status" sub="Move this ticket to a different status">
                                <Box sx={{ display: "grid", gap: 1.5 }}>
                                    <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) minmax(0, 1fr)" } }}>
                                        <Select
                                            size="small"
                                            value={statusDraft}
                                            onChange={(e) => setStatusDraft(e.target.value)}
                                            inputProps={{ "aria-label": "Status" }}
                                            sx={{ minWidth: 0, borderRadius: "10px", fontSize: 14 }}
                                        >
                                            {statusList.map((s) => (
                                                <MenuItem key={s.id} value={s.label}>{s.label}</MenuItem>
                                            ))}
                                        </Select>
                                        <Select
                                            size="small"
                                            value={subStatusDraft}
                                            onChange={(e) => setSubStatusDraft(e.target.value)}
                                            displayEmpty
                                            inputProps={{ "aria-label": "Sub-status" }}
                                            sx={{ minWidth: 0, borderRadius: "10px", fontSize: 14 }}
                                        >
                                            <MenuItem value="">No sub-status</MenuItem>
                                            <MenuItem value="ON_HOLD">On Hold</MenuItem>
                                            <MenuItem value="DELAYED">Delayed</MenuItem>
                                            {/* REOPENED is set automatically on a genuine reopen (see EscalationService.updateStatus),
                                                never chosen manually — shown only so the Select can reflect that current value. */}
                                            {subStatusDraft === "REOPENED" && <MenuItem value="REOPENED">Reopened</MenuItem>}
                                        </Select>
                                    </Box>
                                    <TextField
                                        size="small"
                                        placeholder="Reason (optional)"
                                        value={statusReason}
                                        onChange={(e) => setStatusReason(e.target.value)}
                                        sx={{ "& .MuiInputBase-root": { borderRadius: "10px", fontSize: 14 } }}
                                    />
                                    <LoadingButton
                                        loading={statusSubmitting}
                                        variant="contained"
                                        disabled={!statusDraft || (statusUnchanged && subStatusUnchanged)}
                                        onClick={submitStatusChange}
                                        sx={{ ...gradientBtnSx, justifySelf: "end" }}
                                    >
                                        Update
                                    </LoadingButton>
                                </Box>

                                {history.length > 0 && (
                                    <>
                                        <Divider sx={{ my: 2, borderColor: COLORS.grid }} />
                                        <Typography sx={{ ...LABEL_SX, color: COLORS.purple, mb: 1.5 }}>Status history</Typography>
                                        <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
                                            {history.map((h, i) => {
                                                const tone = statusMeta(statusLabelToCategory[h.toStatus]);
                                                return (
                                                    <Box component="li" key={h.id} sx={{ display: "grid", gridTemplateColumns: "18px minmax(0, 1fr)", gap: 1.25, position: "relative", pb: i === history.length - 1 ? 0 : 2 }}>
                                                        {i < history.length - 1 && (
                                                            <Box aria-hidden sx={{ position: "absolute", left: 8, top: 18, bottom: 0, width: 2, bgcolor: COLORS.grid, borderRadius: 1 }} />
                                                        )}
                                                        <Box aria-hidden sx={{ width: 18, height: 18, mt: 0.25, borderRadius: "50%", bgcolor: tone.soft, border: `2px solid ${tone.solid}`,
                                                            boxShadow: i === 0 ? `0 0 0 4px ${tone.soft}` : "none" }} />
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: 13, color: NAVY, fontWeight: 600, overflowWrap: "anywhere" }}>
                                                                {h.fromStatus ? <>{h.fromStatus} <Box component="span" sx={{ color: MUTED }}>→</Box> <Box component="span" sx={{ color: tone.solid, fontWeight: 800 }}>{h.toStatus}</Box></>
                                                                    : <>Created as <Box component="span" sx={{ color: tone.solid, fontWeight: 800 }}>{h.toStatus}</Box></>}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
                                                                {h.changedAt ? formatDate(h.changedAt) : ""}
                                                                {h.changedByName ? ` · ${h.changedByName}` : ""}
                                                            </Typography>
                                                            {h.reason && (
                                                                <Typography sx={{ fontSize: 13, color: COLORS.ink2, mt: 0.5, px: 1, py: 0.5, borderRadius: "8px", bgcolor: COLORS.ground, overflowWrap: "anywhere" }}>
                                                                    {h.reason}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </>
                                )}
                            </SectionCard>
                        </Box>
                    </Box>

                    {/* Same collapsed-by-default pattern as V1's ViewEms.js — these two sections are
                        supplementary, not always relevant, so they stay out of the way until opened. */}
                    <Accordion disableGutters sx={{ ...ACCORDION_SX, ...rise(3) }}
                        onChange={(_, expanded) => { if (expanded) setShipmentRequested(true); }}>
                        <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />} sx={ACCORDION_SUMMARY_SX}>
                            <Box sx={cardIcSx}><LocalShippingOutlinedIcon /></Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY }}>Shipment (AWB) Details</Typography>
                                <Typography sx={{ fontSize: 12, color: MUTED }}>
                                    The data below provides details of the shipment this ticket was raised for.
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2, borderTop: `1px solid ${COLORS.grid}`, overflowX: "auto" }}>
                            {orderLoading && !orderMatch
                                ? <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={24} sx={{ color: COLORS.purple }} /></Box>
                                : orderMatch
                                ? <ShipmentErrorBoundary><OrderView resData={{ orderSearch }} /></ShipmentErrorBoundary>
                                : <Typography sx={{ textAlign: "center", fontSize: 13, color: MUTED, py: 2 }}>No data</Typography>}
                        </AccordionDetails>
                    </Accordion>

                    {relatedTickets.length > 0 && (
                        <Accordion disableGutters sx={{ ...ACCORDION_SX, ...rise(4) }} TransitionProps={{ unmountOnExit: true }}>
                            <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />} sx={ACCORDION_SUMMARY_SX}>
                                <Box sx={cardIcSx}><LinkOutlinedIcon /></Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY }}>Other Related Tickets</Typography>
                                    <Typography sx={{ fontSize: 12, color: MUTED }}>Tickets with the same AWB.</Typography>
                                </Box>
                                <CountBadge n={relatedTickets.length} />
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, borderTop: `1px solid ${COLORS.grid}`, overflowX: "auto" }}>
                                <ProzoDataGrid
                                    columns={relatedTicketColumns(navigate)}
                                    rows={relatedTickets}
                                    pagination
                                    paginationMode="client"
                                    sizePerPage={relatedSizePerPage}
                                    setSizePerPage={setRelatedSizePerPage}
                                    page={relatedPage}
                                    setPage={setRelatedPage}
                                    hideFooterPagination={false}
                                    rowCount={relatedTickets.length}
                                    autoHeight
                                    getRowId={(row) => row.id}
                                    sx={{ '& .MuiDataGrid-columnHeaderTitle': { fontSize: 12 }, '& .MuiDataGrid-row:hover': { backgroundColor: '#F7F3FB' } }}
                                />
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
            )}
        </>
    );
};

export default ViewTicket;
